import {css, Spinner, view} from '@sail/ui/deprecated';
import {ChangeEvent, useCallback, useMemo, useState} from 'react';
import {defineMessages, useIntl} from 'react-intl';

export const messages = defineMessages({
  submitButton: {
    id: 'support_conversations_client.attachments.submitButton',
    description: 'Label for button that allows user to choose a file',
    defaultMessage: 'Upload',
  },
  submitButtonLoading: {
    id: 'support_conversations_client.attachments.submitButtonLoading',
    description: 'Label for button that indicates an upload is in progress',
    defaultMessage: 'Uploading',
  },
  submitButtonLoadingBatch: {
    id: 'support_conversations_client.attachments.submitButtonLoadingBatch',
    description: 'Label for button that indicates an upload is in progress',
    defaultMessage: 'Uploading ({current} of {total})',
  },
});

type UploadButtonProps = {
  name: string;
  isLoading: boolean;
  uploadBatchTotal: number;
  uploadBatchCurrentIndex: number;
  supportedMimeTypes: string[];
  onFilesSelected: (files: FileList) => void;
};

export const UploadButton = ({
  name,
  isLoading,
  uploadBatchTotal,
  uploadBatchCurrentIndex,
  onFilesSelected,
  supportedMimeTypes,
}: UploadButtonProps): JSX.Element | null => {
  const intl = useIntl();
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const handleFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setIsFocused(true);
      const {files} = e.target;
      if (files && files.length > 0) {
        onFilesSelected(files);
      }
    },
    [onFilesSelected],
  );

  const buttonText = useMemo(() => {
    if (!isLoading) {
      return intl.formatMessage(messages.submitButton);
    }

    if (uploadBatchTotal === 1) {
      return intl.formatMessage(messages.submitButtonLoading);
    }

    return intl.formatMessage(messages.submitButtonLoadingBatch, {
      current: uploadBatchCurrentIndex + 1,
      total: uploadBatchTotal,
    });
  }, [intl, isLoading, uploadBatchTotal, uploadBatchCurrentIndex]);

  return (
    <view.div
      css={{
        position: css.value('relative'),
        height: 500,
        width: 'fill',
      }}
    >
      <view.div>
        <view.input
          id={name}
          type="file"
          multiple
          data-testid="upload-button-file-input"
          name={name}
          accept={supportedMimeTypes.join(',')}
          disabled={isLoading}
          css={{
            position: css.value('absolute'),
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: css.value('0'),
            opacity: css.value('0'),
            cursor: isLoading ? css.value('default') : css.value('pointer'),
          }}
          onChange={handleFileInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        <view.label
          htmlFor={name}
          css={{
            backgroundColor: css.value('$color.brand500'),
            height: 500,
            borderRadius: 'medium',
            color: css.value('$color.neutral0'),
            display: css.value('flex'),
            alignItems: css.value('center'),
            justifyContent: css.value('center'),
            font: 'lead',
            fontWeight: 'semibold',
            boxShadow: isFocused ? 'focus' : 'none',
            opacity: css.value(isLoading ? 0.5 : 1),
          }}
        >
          {isLoading && (
            <Spinner css={{marginRight: 'small', marginTop: 'xsmall'}} />
          )}
          {buttonText}
        </view.label>
      </view.div>
    </view.div>
  );
};
